/*
 * Copyright (c) CIRROM 2026.
 */

import {
  AccountSummaryCompareField,
  AccountSummaryLinkageRow,
  AccountSummaryReferenceField,
} from "../../../api/accounts";
import { NUMBER_FORMATTER, toReadableText } from "../../utils/utils";

/*
 * The display model for one matched reference record.
 *
 * A source hands back its record as a flat inventory of dotted paths
 * ("data.summary.top_tag", "data.filters.workarea_ids"), and the flat list is
 * how the panel used to render it: thirty rows, one column, every label
 * carrying the path as a prefix. The paths are structure, though - the
 * source's own idea of how its facts group - so the model reads them back:
 * one group per path segment, labels with the group's prefix stripped, and
 * the serialisation habits of a REST payload (ISO timestamps, comma-joined
 * lists, a title beside its url, a singular beside its plural) folded into
 * the values a steward would actually want to read.
 *
 * Everything here is derived from the payload's shape, not from a vendor
 * name, so a new source with a different inventory gets the same treatment.
 */

export type SourceValueKind =
  | "metric"
  | "date"
  | "range"
  | "url"
  | "list"
  | "boolean"
  | "text"
  | "empty";

export interface SourceFieldEntry {
  key: string;
  /** The label inside its group, group prefix stripped ("Top Tag"). */
  label: string;
  /** The value as the source serialised it; null when it holds none. */
  value: string | null;
  /** The value as the panel shows it - dates and ranges formatted. */
  display: string | null;
  kind: SourceValueKind;
  /** An enrich field config consumes this field. */
  mapped: boolean;
  /** The configured compare row, when the project maps this field. */
  compare?: AccountSummaryCompareField;
  /** `list` only: the items, in the source's order. */
  items?: string[];
  /**
   * `list` only: the source also named the first item on its own (a "Top
   * Tag" beside "Top Tags"), so the lead item carries that emphasis.
   */
  leadItem?: boolean;
  /**
   * Where the value links: a sibling url field ("Top Article Title" + "...
   * Url"), or a relative path the source gave ("/company/1/officers")
   * resolved against its own record url.
   */
  href?: string;
  /**
   * `date` only, on fields that name a moment ("Last Article Published",
   * "Date Of Cessation"): how long ago that was, in words.
   */
  relative?: string;
}

export interface SourceFieldGroup {
  key: string;
  /** null for the ungrouped remainder - identity, ids, the record itself. */
  label: string | null;
  fields: SourceFieldEntry[];
  /**
   * Unmapped fields the source holds no value for. A mapped empty field
   * stays a row - the project asked for it, so its absence is a fact - but
   * an unmapped one is inventory, and inventory with nothing in it reads
   * best as one line naming what the source could carry.
   */
  emptyFields: SourceFieldEntry[];
}

export interface SourceRecordModel {
  /**
   * The source's one-word verdict on the company - a "Company Status" of
   * "dissolved" is the fact everything else on the panel is read in light
   * of, so it leaves the sheet and sits on the Linked line.
   */
  status: SourceFieldEntry | null;
  /** Mapped metrics: the numbers the project chose as this source's headline. */
  headline: SourceFieldEntry[];
  groups: SourceFieldGroup[];
  /** Every field on screen, tiles and empty lines included. */
  fieldCount: number;
  mappedCount: number;
}

/* ---- Label helpers -------------------------------------------------------- */

/*
 * Field configs commonly carry the source's own prefix
 * ("lexology_article_counts"); inside the source's panel that prefix is
 * noise, so it is stripped for display.
 */
export const enrichedFieldLabel = (fieldName: string, systemName?: string) => {
  const label = toReadableText(fieldName);
  const readableSystem = systemName ? toReadableText(systemName) : "";
  const prefixes = readableSystem
    ? [readableSystem, readableSystem.split(" ")[0]]
    : [];
  for (const prefix of prefixes) {
    if (label.toLowerCase().startsWith(`${prefix.toLowerCase()} `)) {
      return label.slice(prefix.length + 1);
    }
  }
  return label;
};

const stripPrefix = (label: string, prefix: string | null) =>
  prefix && label.toLowerCase().startsWith(`${prefix.toLowerCase()} `)
    ? label.slice(prefix.length + 1)
    : label;

/*
 * Identifier fields ("Crm Account Id", "Company Number", "Sic Codes") are
 * keys and codes: the two sides of a join hold different ids by
 * construction, and a code reformatted as a quantity ("04334460" →
 * "4,334,460") is corrupted. Whole words only: "Account" contains "count"
 * but is not a quantity, and the metric test below shares the rule.
 */
export const isIdentifierLabel = (label: string) =>
  /\b(id|number|no|code|codes|reference|ref)\b/i.test(label);

/*
 * Headline numbers become stat tiles. The label has to say it is a quantity:
 * ids and classification codes (a SIC code, an industry number) are numeric
 * without being metrics, and a reformatted code is corrupted, so unknown
 * numbers keep their ordinary row.
 */
const METRIC_LABEL_PATTERN =
  /\b(counts?|reads|views|totals?|scores?|employees|offices|publications|revenue|articles|interactions)\b/i;

export const isMetricValue = (value: string | null, label: string) =>
  Boolean(value) &&
  /^[\d,]+(\.\d+)?$/.test(value!.trim()) &&
  /* A leading zero is a code's tell; no quantity starts with one. */
  !/^0\d/.test(value!.trim()) &&
  METRIC_LABEL_PATTERN.test(label) &&
  !isIdentifierLabel(label);

export const formatMetric = (value: string) => {
  const parsed = Number(value.replace(/,/g, ""));
  return Number.isNaN(parsed) ? value : NUMBER_FORMATTER.format(parsed);
};

const ISO_DATE =
  /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}(:\d{2})?(\.\d+)?(Z|[+-]\d{2}:?\d{2})?)?$/;

/* Raw ISO timestamps are serialisation, not information. */
const DATE_FORMATTER = new Intl.DateTimeFormat("en-GB", {
  year: "numeric",
  month: "short",
  day: "numeric",
});

const DAY_MONTH_FORMATTER = new Intl.DateTimeFormat("en-GB", {
  month: "short",
  day: "numeric",
});

export const formatSourceDate = (value: string) => {
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? value : DATE_FORMATTER.format(parsed);
};

/* "13 May – 11 Aug 2026" when the year is shared, both years otherwise. */
const formatDateRange = (from: string, to: string) => {
  const start = Date.parse(from);
  const end = Date.parse(to);
  if (Number.isNaN(start) || Number.isNaN(end)) {
    return `${formatSourceDate(from)} – ${formatSourceDate(to)}`;
  }
  const sameYear =
    new Date(start).getFullYear() === new Date(end).getFullYear();
  return sameYear
    ? `${DAY_MONTH_FORMATTER.format(start)} – ${DATE_FORMATTER.format(end)}`
    : `${DATE_FORMATTER.format(start)} – ${DATE_FORMATTER.format(end)}`;
};

/*
 * A date is a fact; how long ago it was is what the steward actually wants
 * from it. Only fields that name a moment get the words - a filing period's
 * end date is bookkeeping, "Last Article Published" is a pulse.
 */
const RECENCY_LABEL_PATTERN =
  /\b(last|latest|published|updated|created|creation|cessation|incorporated|incorporation|dissolved|since|seen)\b/i;

const RELATIVE_FORMATTER = new Intl.RelativeTimeFormat("en-GB", {
  numeric: "auto",
});

const DAY_MS = 24 * 60 * 60 * 1000;

export const formatRelativeDate = (value: string, now: number) => {
  const parsed = Date.parse(value);
  if (Number.isNaN(parsed)) {
    return null;
  }
  const days = Math.round((parsed - now) / DAY_MS);
  const magnitude = Math.abs(days);
  if (magnitude < 7) {
    return RELATIVE_FORMATTER.format(days, "day");
  }
  /* Weeks run to eight: "6 weeks ago" is a fact, "last month" a shrug. */
  if (magnitude < 56) {
    return RELATIVE_FORMATTER.format(Math.round(days / 7), "week");
  }
  if (magnitude < 365) {
    return RELATIVE_FORMATTER.format(Math.round(days / 30.44), "month");
  }
  return RELATIVE_FORMATTER.format(Math.round(days / 365.25), "year");
};

/*
 * Some sources hand back list literals ("['74990', '12345']"); the brackets
 * and quotes are serialisation, not information. A comma-joined string with
 * more than one part is a list too. Anything else is a single value.
 */
const splitList = (value: string): string[] | null => {
  const trimmed = value.trim();
  const body = /^\[.*\]$/.test(trimmed) ? trimmed.slice(1, -1) : trimmed;
  if (!/^\[.*\]$/.test(trimmed) && !body.includes(", ")) {
    return null;
  }
  const parts = body
    .split(",")
    .map((part) => part.trim().replace(/^['"]|['"]$/g, ""))
    .filter(Boolean);
  return parts.length > 1 ? parts : null;
};

/** Bracketed list literals flattened; everything else passes through. */
export const formatSourceValue = (value: string) => {
  const parts = /^\[.*\]$/.test(value.trim()) ? splitList(value) : null;
  return parts ? parts.join(", ") : value;
};

interface ClassifyContext {
  /** The source's own record url; relative paths resolve against its origin. */
  sourceOrigin: string | null;
  now: number;
}

const classify = (
  entry: Pick<SourceFieldEntry, "label" | "value">,
  context: ClassifyContext
): Pick<
  SourceFieldEntry,
  "kind" | "display" | "items" | "href" | "relative"
> => {
  const { label, value } = entry;
  if (value == null || value.trim() === "") {
    return { kind: "empty", display: null };
  }
  const trimmed = value.trim();
  if (isMetricValue(value, label)) {
    return { kind: "metric", display: formatMetric(value) };
  }
  if (ISO_DATE.test(trimmed)) {
    return {
      kind: "date",
      display: formatSourceDate(trimmed),
      relative: RECENCY_LABEL_PATTERN.test(label)
        ? formatRelativeDate(trimmed, context.now) ?? undefined
        : undefined,
    };
  }
  if (/^https?:\/\/\S+$/i.test(trimmed)) {
    return { kind: "url", display: trimmed, href: trimmed };
  }
  /*
   * A path rather than a url ("/company/1/officers") is the source pointing
   * within itself; with the record's own url in hand the origin is known,
   * and the path becomes the one-click route to the officers, the filings,
   * the insolvency record.
   */
  if (context.sourceOrigin && /^\/\S+$/.test(trimmed)) {
    return {
      kind: "url",
      display: trimmed,
      href: context.sourceOrigin + trimmed,
    };
  }
  /* "True"/"False" is a serialised boolean; the steward reads yes or no. */
  if (/^(true|false)$/i.test(trimmed)) {
    return { kind: "boolean", display: /^true$/i.test(trimmed) ? "Yes" : "No" };
  }
  const items = splitList(value);
  if (items) {
    return { kind: "list", display: items.join(", "), items };
  }
  return {
    kind: "text",
    display: isIdentifierLabel(label) ? value : readableEnum(trimmed),
  };
};

/*
 * A short, all-lowercase, letters-only, space-free value ("england-wales",
 * "ltd", "dissolved") is the source's enum, not prose; it reads back as
 * words. Anything with a capital, a digit, a space or real length is left
 * exactly as the source wrote it - a title is not ours to recase, and a
 * code ("ref-1") is not ours to reword. Identifier fields skip it entirely.
 */
const readableEnum = (value: string) =>
  value.length <= 32 && /^[a-z]+([-_][a-z]+)*$/.test(value)
    ? value
        .split(/[-_]/)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ")
    : value;

/*
 * Serialisation that carries nothing a steward can act on: an etag, a
 * "self" link back to the record already linked from the Linked line, a
 * bare content hash. Dropped before grouping, and not counted.
 */
const isNoiseField = (fieldName: string, value: string | null) =>
  /(^|\.)(etag|self)$/i.test(fieldName) ||
  Boolean(value && /^[0-9a-f]{32,}$/i.test(value.trim()));

const originOf = (url: string | null | undefined) => {
  if (!url) {
    return null;
  }
  try {
    return new URL(url).origin;
  } catch {
    return null;
  }
};

/*
 * Which field is the source's verdict: a "status" in its label, holding a
 * single word or short phrase. The first one wins - a source states its
 * verdict once.
 */
const isStatusEntry = (entry: SourceFieldEntry) =>
  entry.kind === "text" &&
  /\bstatus\b/i.test(entry.label) &&
  (entry.display ?? "").length <= 32;

const MONTH_FORMATTER = new Intl.DateTimeFormat("en-GB", { month: "short" });

/* ---- Grouping ------------------------------------------------------------ */

/*
 * "data.summary.top_tag" → group "summary". The leading "data" is the
 * envelope every field of a REST payload sits in, not a group, so it is
 * skipped; a path with nothing left before its leaf is ungrouped.
 */
const groupOf = (fieldName: string): string | null => {
  const path = fieldName.split(".");
  if (path[0] === "data") {
    path.shift();
  }
  return path.length > 1 ? path[0] : null;
};

const dedupeKey = (label: string, value: string | null) =>
  `${label.trim().toLowerCase()}|${(value ?? "").trim()}`;

/*
 * Pairs that read as one fact. Applied per group, on the display labels,
 * so "Date From" + "Date To" in one group become a single "Date" range even
 * when another group carries its own pair.
 */
const foldPairs = (fields: SourceFieldEntry[]): SourceFieldEntry[] => {
  const byLabel = new Map(
    fields.map((field) => [field.label.toLowerCase(), field])
  );
  const dropped = new Set<SourceFieldEntry>();
  const folded: SourceFieldEntry[] = [];

  for (const field of fields) {
    if (dropped.has(field)) {
      continue;
    }
    const lower = field.label.toLowerCase();

    /* "X From" + "X To" → X, a range. */
    if (field.kind === "date" && lower.endsWith(" from")) {
      const to = byLabel.get(`${lower.slice(0, -5)} to`);
      if (to && to.kind === "date") {
        dropped.add(to);
        folded.push({
          ...field,
          key: `${field.key}+${to.key}`,
          label: field.label.slice(0, -5),
          kind: "range",
          display: formatDateRange(field.value!, to.value!),
          mapped: field.mapped || to.mapped,
        });
        continue;
      }
    }

    /* "X Day" + "X Month" → X, as a day in a month ("31 May"). */
    if (lower.endsWith(" day") && /^\d{1,2}$/.test(field.value?.trim() ?? "")) {
      const month = byLabel.get(`${lower.slice(0, -4)} month`);
      const monthNumber = Number(month?.value?.trim());
      if (month && monthNumber >= 1 && monthNumber <= 12) {
        dropped.add(month);
        folded.push({
          ...field,
          key: `${field.key}+${month.key}`,
          label: field.label.slice(0, -4),
          kind: "text",
          display: `${Number(field.value!.trim())} ${MONTH_FORMATTER.format(
            new Date(Date.UTC(2000, monthNumber - 1, 15))
          )}`,
          mapped: field.mapped || month.mapped,
        });
        continue;
      }
    }

    /* "X Title" + "X Url" → the title, linked. */
    if (field.kind === "text" && lower.endsWith(" title")) {
      const url = byLabel.get(`${lower.slice(0, -6)} url`);
      if (url && url.kind === "url") {
        dropped.add(url);
        folded.push({ ...field, href: url.value! });
        continue;
      }
    }

    /*
     * "X" + "Xs", where X is the first of the Xs: the singular is the
     * source naming its lead item, so the list carries it as emphasis. The
     * singular's mapping travels with it - a "Top Contributor" the project
     * maps is still the mapped fact, now shown as the lead of its list.
     */
    if (field.kind === "list" && lower.endsWith("s")) {
      const singular = byLabel.get(lower.slice(0, -1));
      if (
        singular &&
        singular.kind === "text" &&
        singular.value!.trim().toLowerCase() === field.items![0].toLowerCase()
      ) {
        dropped.add(singular);
        folded.push({
          ...field,
          leadItem: true,
          mapped: field.mapped || singular.mapped,
          compare: field.compare ?? singular.compare,
        });
        continue;
      }
    }

    folded.push(field);
  }
  /* A singular can precede its plural; a second pass drops what was folded. */
  return folded.filter((field) => !dropped.has(field));
};

export const buildSourceRecordModel = (
  row: AccountSummaryLinkageRow,
  systemName?: string,
  /** Injectable for tests; relative dates are read against it. */
  now: number = Date.now()
): SourceRecordModel => {
  const fieldCompare = row.field_compare;
  const referenceFields: AccountSummaryReferenceField[] =
    fieldCompare?.reference_record?.fields ?? [];
  const context: ClassifyContext = {
    sourceOrigin: originOf(fieldCompare?.reference_record?.source_record_url),
    now,
  };
  const compareFields = fieldCompare?.fields ?? [];
  const compareByLabel = new Map(
    compareFields
      .filter((field) => field.data_source_field_label)
      .map((field) => [field.data_source_field_label!.toLowerCase(), field])
  );

  /*
   * Insertion order is the source's order, which is the order it thinks in
   * (top tag, then the tags, then their count). Duplicates - a source that
   * states its company name at two paths, a configured id that repeats an
   * inventory field - collapse onto the first occurrence, and the mapped
   * copy wins so the CRM comparison survives the merge.
   */
  const entries = new Map<
    string,
    SourceFieldEntry & { group: string | null }
  >();
  const joined = new Set<AccountSummaryCompareField>();

  const put = (entry: SourceFieldEntry & { group: string | null }) => {
    const key = dedupeKey(entry.label, entry.value);
    const existing = entries.get(key);
    if (!existing) {
      entries.set(key, entry);
    } else if (entry.mapped && !existing.mapped) {
      entries.set(key, { ...entry, group: existing.group });
    }
  };

  referenceFields.forEach((field) => {
    if (isNoiseField(field.field_name, field.value)) {
      return;
    }
    const compare = compareByLabel.get(field.label.toLowerCase());
    if (compare) {
      joined.add(compare);
    }
    const group = groupOf(field.field_name);
    const label = stripPrefix(
      field.label,
      group ? toReadableText(group) : null
    );
    put({
      key: `ref-${field.field_name}`,
      label,
      value: field.value,
      mapped: field.is_mapped || Boolean(compare),
      compare,
      group,
      ...classify({ label, value: field.value }, context),
    });
  });

  /*
   * Configured fields with no inventory counterpart (identity mappings like
   * "Crm Account Id", or every field on an older payload that carries only
   * the compare rows) are appended rather than dropped.
   */
  compareFields
    .filter((field) => !joined.has(field))
    .forEach((field) => {
      const label = enrichedFieldLabel(field.field_name, systemName);
      put({
        key: `cfg-${field.field_config_id}`,
        label,
        value: field.data_source_value,
        mapped: true,
        compare: field,
        group: null,
        ...classify({ label, value: field.data_source_value }, context),
      });
    });

  const all = Array.from(entries.values());
  const status = all.find(isStatusEntry) ?? null;
  const headline = all.filter(
    (entry) => entry.mapped && entry.kind === "metric"
  );
  const lifted = new Set(
    [...headline, ...(status ? [status] : [])].map((entry) => entry.key)
  );

  /*
   * An address arrives as a group of lines ("Registered Office Address" →
   * Address Line 1, Address Line 2, Postal Code). Nobody reads an address
   * as three labelled rows, so the group becomes one identity row holding
   * the lines in the source's order, and the empty lines simply fall away.
   */
  const addressGroups = new Set(
    all
      .map((entry) => entry.group)
      .filter(
        (group): group is string => Boolean(group) && /address/i.test(group!)
      )
  );
  const addressRows: (SourceFieldEntry & { group: string | null })[] =
    Array.from(addressGroups).flatMap((group) => {
      const lines = all.filter(
        (entry) => entry.group === group && entry.kind === "text"
      );
      if (lines.length === 0) {
        return [];
      }
      const label = toReadableText(group);
      const value = lines.map((line) => line.display).join(", ");
      return [
        {
          key: `addr-${group}`,
          label,
          value,
          display: value,
          kind: "text" as const,
          mapped: lines.some((line) => line.mapped),
          compare: lines.find((line) => line.compare)?.compare,
          group: null,
        },
      ];
    });
  all.forEach((entry) => {
    if (entry.group && addressGroups.has(entry.group)) {
      lifted.add(entry.key);
    }
  });
  addressRows.forEach((entry) => all.push(entry));

  /*
   * The ungrouped fields lead: they are the record's identity - its name,
   * its id, the window it covers - and read straight off the Linked line
   * without needing a heading of their own. Named groups follow in the
   * source's order, except that a group with nothing in it sinks to the
   * end: an inventory of empty slots should not be the first thing read.
   */
  const groupOrder: (string | null)[] = [null];
  all.forEach((entry) => {
    if (!groupOrder.includes(entry.group)) {
      groupOrder.push(entry.group);
    }
  });

  const groups: SourceFieldGroup[] = groupOrder
    .map((group) => {
      const members = foldPairs(
        all.filter((entry) => entry.group === group && !lifted.has(entry.key))
      );
      return {
        key: group ?? "record",
        label: group ? toReadableText(group) : null,
        fields: members.filter(
          (entry) => entry.kind !== "empty" || entry.mapped
        ),
        emptyFields: members.filter(
          (entry) => entry.kind === "empty" && !entry.mapped
        ),
      };
    })
    .filter((group) => group.fields.length + group.emptyFields.length > 0)
    .sort(
      (a, b) => Number(a.fields.length === 0) - Number(b.fields.length === 0)
    );

  const fieldCount =
    headline.length +
    (status ? 1 : 0) +
    groups.reduce(
      (count, group) => count + group.fields.length + group.emptyFields.length,
      0
    );

  return {
    status,
    headline,
    groups,
    fieldCount,
    mappedCount: all.filter((entry) => entry.mapped).length,
  };
};

/*
 * How the match was found, in the product's own words. The enum reads back as
 * "Cirrom Proposal", which is a status label for something that is really a
 * provenance fact - and the ledger already has the plain phrasing for a
 * suggestion Cirrom made ("Cirrom proposes"), so the linkage rows use the same
 * voice. Anything else keeps its readable enum, which is all we know about it.
 */
export const searchTypeLabel = (searchType: string | null) => {
  if (!searchType) {
    return null;
  }
  return searchType.toUpperCase() === "CIRROM_PROPOSAL"
    ? "Proposed by Cirrom"
    : toReadableText(searchType);
};
